import withDrawer, {DrawerCloseButton, SideDrawerProps} from "../../../../common/components/SideDrawerView";
import React from "react";
import {Box, CircularProgress, FormControl, IconButton, List, Typography} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import WordUpdateView from "../WordUpdateView/WordUpdateView";
import DeleteIcon from "@mui/icons-material/Delete";
import WordDeleteView from "../WordDeleteView/WordDeleteView";
import {VocabularyActionDispatch} from "../../pages/Vocabulary";
import {useAuth} from "../../../auth/context/AuthContext";
import InfoHeaderView from "./components/InfoHeaderView";
import TranslationsView from "./components/TranslationsView";
import ContextSentenceView from "./components/ContextSentenceView";
import SynonymsView from "./components/SynonymsView";
import SrsInfoView from "./components/SrsInfoView";
import {useWordInfoView, UseWordInfoViewWord} from "./hooks/useWordInfoView";
import {BasicWordWithId} from "../../../../common/types/words";
import UnitTopicView from "./components/UnitTopicView";
import ConjugationView from "./components/ConjugationView";

export type WordInfoViewWord = UseWordInfoViewWord;

export interface WordInfoViewProps extends SideDrawerProps {
    word: WordInfoViewWord;
    actionDispatch?: VocabularyActionDispatch
}

function WordInfoViewContent({word, actionDispatch, onDrawerClosed}: WordInfoViewProps) {

    const [displayUpdateView, setDisplayUpdateView] = React.useState<boolean>(false);
    const [displayDeleteView, setDisplayDeleteView] = React.useState<boolean>(false);
    const [selectedSynonym, setSelectedSynonym] = React.useState<BasicWordWithId | null>(null);

    const {isAdmin} = useAuth();

    const {
        queryClient,
        currentDeck,
        fullWordInfo,
        isPending,
        isBasicWordInfo,
        usedWord,
        audio,
        sentenceAudio,
        isWordLearned,
        handlePlayAudio,
    } = useWordInfoView({word});

    if (selectedSynonym) {
        return (
            <WordInfoView
                word={selectedSynonym}
                actionDispatch={actionDispatch}
                closeButton={DrawerCloseButton.BACK}
                onDrawerClosed={(isViaCloseButton) => {
                    setSelectedSynonym(null);
                    if (!isViaCloseButton) {
                        onDrawerClosed(false);
                    }
                }}
                backdropInvisible={true}
            />
        );
    }

    if (displayUpdateView) {
        return (
            <WordUpdateView word={usedWord}
                            closeButton={DrawerCloseButton.BACK}
                            onDrawerClosed={(isViaCloseButton) => {
                                setDisplayUpdateView(false);
                                if (!isViaCloseButton) {
                                    onDrawerClosed(false);
                                }
                            }}
                            snackbar={{showRequestSentSnackbar: true, showRequestSuccessSnackbar: true, showRequestErrorSnackbar: true}}
                            actionDispatch={{
                                update: (updatedWord, updatedWordPromise) => {
                                    updatedWordPromise.then(() => queryClient.invalidateQueries({queryKey: ["word-info", word.wordId]}));
                                    setDisplayUpdateView(false);
                                    !!actionDispatch?.update && actionDispatch.update(updatedWord, updatedWordPromise);
                                }
                            }}
                            backdropInvisible={true}
            />
        );
    }

    if (displayDeleteView) {
        return (
            <WordDeleteView word={usedWord}
                            closeButton={DrawerCloseButton.BACK}
                            onDrawerClosed={(isViaCloseButton) => {
                                setDisplayDeleteView(false);
                                if (!isViaCloseButton) {
                                    onDrawerClosed(false);
                                }
                            }}
                            actionDispatch={{
                                delete: (onDeletedPromise) => {
                                    setDisplayDeleteView(false);
                                    !!actionDispatch?.delete && actionDispatch.delete(onDeletedPromise);
                                }
                            }}
                            snackbar={{showRequestSentSnackbar: true, showRequestSuccessSnackbar: true, showRequestErrorSnackbar: true}}
                            backdropInvisible={true}
            />
        );
    }

    return (
        <FormControl sx={{width: '100%'}}>
            <Box sx={{
                position: "absolute",
                top: 1,
                right: 8,
                zIndex: 1,
            }}>
                {isAdmin && (
                    <IconButton
                        onClick={() => setDisplayUpdateView(true)}
                        size={"small"}
                        sx={{color: "black", mr: 1}}
                    >
                        <EditIcon fontSize={"small"}/>
                    </IconButton>
                )}
                <IconButton
                    onClick={() => setDisplayDeleteView(true)}
                    size={"small"}
                    sx={{color: "black"}}
                >
                    <DeleteIcon fontSize={"small"}/>
                </IconButton>
            </Box>
            <Typography variant="h5" align="center" fontWeight="bold">Word info</Typography>
            <Box sx={{mt: 4}}/>
            <List disablePadding>
                <InfoHeaderView language={currentDeck!.language}
                                          word={usedWord}
                                          audio={audio}
                                          showPlaybackButton={!!audio || usedWord.resources?.lemma === null}
                                          onPlayAudio={handlePlayAudio}/>
                <TranslationsView wordId={usedWord.wordId} translations={usedWord.translations} userTranslations={usedWord.userTranslations}/>
                {!isBasicWordInfo || (!!fullWordInfo && !isPending) ? (
                    <>
                        <ContextSentenceView sentence={usedWord.exampleSentences?.[0]?.sentence}
                                            translation={usedWord.exampleSentences?.[0]?.translation}
                                            audio={sentenceAudio}
                                            showPlaybackButton={!!sentenceAudio || usedWord.resources?.sentence === null}
                                            onPlayAudio={handlePlayAudio}/>
                        <SynonymsView
                            synonyms={fullWordInfo?.synonyms}
                            onSynonymClick={(synonym) => setSelectedSynonym(synonym)}
                        />
                        <UnitTopicView unit={fullWordInfo?.unit} topic={fullWordInfo?.topic} />
                        {!!fullWordInfo && (
                            <SrsInfoView fullWordInfo={fullWordInfo} isWordLearned={isWordLearned}/>
                        )}
                        <ConjugationView word={usedWord} language={currentDeck!.language} />
                    </>
                ) : null}
            </List>
            {isPending && loadingView()}
        </FormControl>
    );

    function loadingView() {
        return (
            <Box sx={{display: "flex", justifyContent: "start", alignItems: "start", height: "100%", mt: 3, ml: 3}}>
                <CircularProgress size={20}/>
            </Box>
        );
    }
}

const WordInfoView = withDrawer(WordInfoViewContent);

export default WordInfoView;